import { useQuery } from '@tanstack/react-query'
import { gmailService } from '../services/gmailService'
import type { GmailLabel } from '../types'

// Helper to safely extract labels array from any response format
function extractLabelsArray(data: unknown): GmailLabel[] {
  if (!data) return []
  if (Array.isArray(data)) return data
  if (typeof data === 'object' && data !== null) {
    // Handle { data: [...] } or { success: true, data: [...] } formats
    const obj = data as Record<string, unknown>
    if (Array.isArray(obj.data)) return obj.data as GmailLabel[]
    if (Array.isArray(obj.labels)) return obj.labels as GmailLabel[]
  }
  return []
}

export function useLabels() {
  const query = useQuery({
    queryKey: ['gmail-labels'],
    queryFn: () => gmailService.getLabels(),
    staleTime: 60000, // Cache for 1 minute
    gcTime: 300000, // Keep in cache for 5 minutes
  })

  // Safely extract labels array from response
  const labelsData = extractLabelsArray(query.data)

  // Filter to show only user-friendly labels
  const userLabels = labelsData.filter((label: GmailLabel) => {
    // Show user-created labels and useful system labels
    if (label.type === 'user') return true
    // Show specific system labels that are useful
    const usefulSystemLabels = ['STARRED', 'IMPORTANT', 'INBOX', 'SPAM', 'TRASH', 'UNREAD']
    return usefulSystemLabels.includes(label.id)
  })

  return {
    labels: labelsData,
    userLabels,
    isLoading: query.isLoading,
    error: query.error,
  }
}
